import * as LocalAuthentication from 'expo-local-authentication';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';

import { StackNavigationProp } from '@react-navigation/stack';

import biometrics from '../biometrics/biometrics';
import { useLogout } from '../hooks/user/useLogout';
import { useUserContext } from './UserContext';
import { useUserSesstionTimeout } from '../hooks/user/useUserSessionTimeout';
import { StackActions } from '@react-navigation/native';

export const UserSessionContext = createContext(null);

export const useUserSessionContext = () => {
  const context = useContext(UserSessionContext);
  if (!context) {
    throw new Error(
      'useUserSessionContext must be used within the context of the UserSessionProvider component'
    );
  }
  return context;
};

export const UserSessionProvider: React.FC<{
  children: React.ReactNode;
  navigation: StackNavigationProp<any>;
}> = ({ children, navigation }) => {
  const appState = useRef(AppState.currentState);
  const mountRef = useRef(false);
  const { user } = useUserContext();
  const { isLoggedOut, logout } = useLogout();
  const { updateExpireTime, isSessionExpired } = useUserSesstionTimeout();

  useEffect(() => {
    (async () => {
      if (Platform.OS === 'web') return;
      if (!user) return;
      if (!mountRef.current) {
        const biometricLoginEnabled = await biometrics.getBiometricLogin();
        const hasEnrolledBiometric =
          await LocalAuthentication.isEnrolledAsync();
        if (biometricLoginEnabled && hasEnrolledBiometric) return;
        if (await isSessionExpired()) logout({ reason: 'session-expired' });
        mountRef.current = true;
      }
    })();
  }, [logout, user, isSessionExpired]);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (!user) return;
    const handleAppStateChange = async (nextAppState: AppStateStatus) => {
      const biometricLoginEnabled = await biometrics.getBiometricLogin();
      const hasEnrolledBiometric = await LocalAuthentication.isEnrolledAsync();
      if (biometricLoginEnabled && hasEnrolledBiometric) return;
      if (appState.current.match(/background/) && nextAppState === 'active') {
        if (await isSessionExpired()) logout({ reason: 'session-expired' });
      } else if (
        appState.current.match(/active/) &&
        nextAppState === 'background'
      ) {
        updateExpireTime();
      }
      appState.current = nextAppState;
    };

    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, [logout, navigation, updateExpireTime, user, isSessionExpired]);

  useEffect(() => {
    if (isLoggedOut) {
      navigation?.dispatch(
        StackActions.replace('IntroNavigator', {
          screen: 'LoginScreen'
        })
      );
    }
  }, [isLoggedOut, navigation]);

  return (
    <UserSessionContext.Provider value={null}>
      {children}
    </UserSessionContext.Provider>
  );
};
